// apps/web/src/lib/roles.ts
// Shared role definitions and utilities for role dropdowns.

/** System role definitions matching the Prisma ShowRoleName enum. */
export const SYSTEM_ROLES: Array<{ value: string; label: string; group: string }> = [
  // Artist and music
  { value: 'principal_artist', label: 'Principal Artist', group: 'artist_and_music' },
  { value: 'musical_director', label: 'Musical Director', group: 'artist_and_music' },
  { value: 'backing_singer', label: 'Backing Singer', group: 'artist_and_music' },
  { value: 'band_musician', label: 'Band Musician', group: 'artist_and_music' },
  // Management and ops
  { value: 'tour_manager', label: 'Tour Manager', group: 'management_and_ops' },
  { value: 'production_manager', label: 'Production Manager', group: 'management_and_ops' },
  { value: 'personal_assistant', label: 'Personal Assistant', group: 'management_and_ops' },
  { value: 'artist_security', label: 'Artist Security', group: 'management_and_ops' },
  // Sound
  { value: 'foh_engineer', label: 'FOH Engineer', group: 'sound' },
  { value: 'monitor_engineer', label: 'Monitor Engineer', group: 'sound' },
  { value: 'playback_engineer', label: 'Playback Engineer', group: 'sound' },
  { value: 'sound_tech', label: 'Sound Tech', group: 'sound' },
  // Lighting and visuals
  { value: 'lighting_director', label: 'Lighting Director', group: 'lighting_and_visuals' },
  { value: 'lighting_operator', label: 'Lighting Operator', group: 'lighting_and_visuals' },
  { value: 'video_director', label: 'Video Director', group: 'lighting_and_visuals' },
  { value: 'spotlight_operator', label: 'Spotlight Operator', group: 'lighting_and_visuals' },
  // Stage and backline
  { value: 'stage_manager', label: 'Stage Manager', group: 'stage_and_backline' },
  { value: 'backline_tech', label: 'Backline Tech', group: 'stage_and_backline' },
  { value: 'guitar_tech', label: 'Guitar Tech', group: 'stage_and_backline' },
  { value: 'drum_tech', label: 'Drum Tech', group: 'stage_and_backline' },
  // Wardrobe and personal
  { value: 'wardrobe_supervisor', label: 'Wardrobe Supervisor', group: 'wardrobe_and_personal' },
  { value: 'hair_makeup', label: 'Hair & Makeup', group: 'wardrobe_and_personal' },
  { value: 'catering_manager', label: 'Catering Manager', group: 'wardrobe_and_personal' },
  { value: 'medic', label: 'Medic', group: 'wardrobe_and_personal' },
  // Local production
  { value: 'head_rigger', label: 'Head Rigger', group: 'local_production' },
  { value: 'rigger', label: 'Rigger', group: 'local_production' },
  { value: 'stagehand', label: 'Stagehand', group: 'local_production' },
  { value: 'truck_driver', label: 'Truck Driver', group: 'local_production' },
  { value: 'catering_crew', label: 'Catering Crew', group: 'local_production' },
  { value: 'dressing_room_runner', label: 'Dressing Room Runner', group: 'local_production' },
  { value: 'local_driver', label: 'Local Driver', group: 'local_production' },
  { value: 'interpreter', label: 'Interpreter', group: 'local_production' },
  { value: 'loadout_crew', label: 'Loadout Crew', group: 'local_production' },
  { value: 'local_security', label: 'Local Security', group: 'local_production' },
  // Venue staff
  { value: 'box_office', label: 'Box Office', group: 'venue_staff' },
  { value: 'ticket_scanner', label: 'Ticket Scanner', group: 'venue_staff' },
  { value: 'usher_steward', label: 'Usher / Steward', group: 'venue_staff' },
  { value: 'bar_staff', label: 'Bar Staff', group: 'venue_staff' },
  { value: 'merch_seller', label: 'Merch Seller', group: 'venue_staff' },
  { value: 'venue_manager', label: 'Venue Manager', group: 'venue_staff' },
  { value: 'venue_technician', label: 'Venue Technician', group: 'venue_staff' },
  { value: 'crowd_security', label: 'Crowd Security', group: 'venue_staff' },
]

/** Human-readable labels for ShowRoleGroup enum values. */
export const GROUP_LABELS: Record<string, string> = {
  artist_and_music: 'Artist & Music',
  management_and_ops: 'Management & Ops',
  sound: 'Sound',
  lighting_and_visuals: 'Lighting & Visuals',
  stage_and_backline: 'Stage & Backline',
  wardrobe_and_personal: 'Wardrobe & Personal',
  local_production: 'Local Production',
  venue_staff: 'Venue Staff',
}

/**
 * Converts a role value (system or custom) to a human-readable label.
 * System roles use the static lookup; custom roles strip the "custom:"
 * prefix and title-case the slug.
 */
export function roleLabel(value: string): string {
  const system = SYSTEM_ROLES.find((r) => r.value === value)
  if (system) return system.label
  // Custom role: strip "custom:" prefix and titlecase
  const name = value.replace(/^custom:/, '').replace(/-/g, ' ')
  return name.charAt(0).toUpperCase() + name.slice(1)
}
